import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orgApi, type Organization } from '../../api/organizations';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  suspended: 'bg-amber-100 text-amber-800',
  archived: 'bg-slate-100 text-slate-700',
  deleting: 'bg-red-100 text-red-800',
};

export function OrganizationListPage() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = query.trim()
        ? (await orgApi.search({ q: query.trim(), ...(status ? { status } : {}) })).results
        : await orgApi.list(status ? { status, q: query || undefined } : { q: query || undefined });
      setOrgs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    void load();
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Organizations</h1>
          <p className="mt-1 text-sm text-slate-600">Manage enterprise organizations, lifecycle, and settings.</p>
        </div>
        <Link
          to="/organizations/new"
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Create organization
        </Link>
        <Link
          to="/organizations/search"
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
        >
          Advanced search
        </Link>
      </div>

      <form onSubmit={onSearch} className="mt-6 flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Search name, legal name, GST, tags…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-300 px-3 py-2"
          aria-label="Search organizations"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        <button type="submit" className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">
          Search
        </button>
      </form>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {loading ? (
        <p className="mt-6 text-slate-600">Loading…</p>
      ) : orgs.length === 0 ? (
        <p className="mt-6 text-slate-600">No organizations found.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Legal name</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/organizations/${o.id}`} className="font-medium text-slate-900 hover:underline">
                      {o.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{o.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[o.status] ?? 'bg-slate-100'}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{o.profile?.legal_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(o.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
